// Implements: F-GEN-033, F-GEN-034, F-UIX-025, F-UIX-009
/**
 * The diagram tab of the editor's drawer (mockup v3 P6, `diagramPane`): a version picker and the
 * two diagrams of the chosen version.
 *
 * Told what the page knows: the mockup's current version (`null` when it has none, `undefined`
 * while unknown). The picker lists every version, newest first, the current one marked
 * "(actual)"; it starts on the current version and follows it until the person picks another.
 * Only the current version is drawn (P6-1); an older one shows what it stored, read-only.
 *
 * The version's diagrams are rendered under the key `mockupId:versionId`, so what happens on one
 * version (a refusal, a typed brief, a draw under way) never shows on another (checklist 14).
 * The chosen tab is kept across versions, as v3 keeps one tab for the drawer. It is not stored:
 * DD22 allows no new browser-storage key.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Select } from "../../../shared/ui/atoms/Select";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { DiagramKind } from "../../../types/diagrams";
import { useMockupBrief, useVersionNumbers, useVersionPieces } from "../hooks/useDrawerReads";
import type { VersionNumber } from "../hooks/useDrawerReads";
import { DiagramContainer } from "./DiagramContainer";
import styles from "./DiagramContainer.module.css";

export interface DiagramDrawerContainerProps {
  readonly mockupId: string;
  /** The mockup's current version: `null` when it has none, `undefined` while unknown. */
  readonly currentVersionId: string | null | undefined;
  /** v3 "Compartir" of a drawn journey: the page opens its share control on that version's diagram. */
  readonly onShare?: ((versionId: string) => void) | undefined;
}

const VERSION_SELECT_ID = "diagram-version";

export function DiagramDrawerContainer({
  mockupId,
  currentVersionId,
  onShare,
}: DiagramDrawerContainerProps): ReactElement {
  const { t } = useTranslation();
  if (currentVersionId === undefined) return <LoadingState label={t("common.state.loading")} />;
  if (currentVersionId === null) {
    return (
      <section className={styles.diagram}>
        <p className={styles.note}>{t("diagram.refusal.noVersion")}</p>
      </section>
    );
  }
  return <VersionDiagrams key={mockupId} mockupId={mockupId} currentVersionId={currentVersionId} onShare={onShare} />;
}

function VersionDiagrams({
  mockupId,
  currentVersionId,
  onShare,
}: {
  readonly mockupId: string;
  readonly currentVersionId: string;
  readonly onShare: ((versionId: string) => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  // `null`: follow the current version (a new generation moves it).
  const [picked, setPicked] = useState<string | null>(null);
  const [tab, setTab] = useState<DiagramKind>("journey");
  const shown = picked ?? currentVersionId;
  const versions = useVersionNumbers(mockupId);
  const pieces = useVersionPieces(mockupId, shown);
  const brief = useMockupBrief(mockupId);

  const numberOf = (id: string): number | undefined =>
    versions.find((version) => version.id === id)?.n ??
    (pieces.status === "read" && pieces.detail?.id === id ? pieces.detail.sequence_number : undefined);
  const listed: readonly VersionNumber[] = versions.some((version) => version.id === shown)
    ? versions
    : [{ id: shown, n: numberOf(shown) ?? 0 }, ...versions];
  const options = listed.map((version) => ({
    value: version.id,
    label: t(version.id === currentVersionId ? "diagram.version.optionCurrent" : "diagram.version.option", {
      n: String(version.n),
    }),
  }));

  return (
    <section className={styles.diagram}>
      <div className={styles.bar}>
        <label className={styles.note} htmlFor={VERSION_SELECT_ID}>
          {t("diagram.version.label")}
        </label>
        <Select
          id={VERSION_SELECT_ID}
          value={shown}
          options={options}
          onChange={(id) => {
            setPicked(id === currentVersionId ? null : id);
          }}
        />
        <span className={styles.note}>{t("diagram.version.hint")}</span>
      </div>
      {pieces.status === "pending" ? (
        <LoadingState label={t("common.state.loading")} />
      ) : (
        <DiagramContainer
          key={`${mockupId}:${shown}`}
          mockupId={mockupId}
          versionId={shown}
          versionNumber={numberOf(shown) ?? 0}
          artifacts={pieces.detail?.artifacts ?? []}
          drawable={shown === currentVersionId}
          defaultBrief={brief}
          tab={tab}
          onTabChange={setTab}
          onShare={onShare}
        />
      )}
    </section>
  );
}
